import * as XLSX from 'xlsx'
import type { ComputedEmployeeUserStats } from './computeEmployeeUserStats'
import { MONTH_NAMES } from './projectAnalysisPeriod'

function safeFilename(base: string) {
  const safe = (base || 'export')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80)
  return safe || 'export'
}

function csvLine(arr: (string | number)[]) {
  return arr
    .map((v) => {
      const s = String(v ?? '')
      if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    })
    .join(',')
}

export function exportRowsToCsv(filenameBase: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return
  const headers = Object.keys(rows[0])
  const bom = '\uFEFF'
  const csv =
    bom +
    csvLine(headers) +
    '\n' +
    rows.map((r) => csvLine(headers.map((h) => r[h] as string | number))).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${safeFilename(filenameBase)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function exportRowsToExcel(
  filenameBase: string,
  sheetName: string,
  rows: Record<string, unknown>[],
) {
  if (!rows.length) return
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31))
  XLSX.writeFile(wb, `${safeFilename(filenameBase)}.xlsx`)
}

export function getUserWiseExportRows(
  rows: Array<{
    full_name?: string
    username?: string
    is_active: boolean
    total_hours: number
    billable_hours: number
    non_billable_hours: number
    utilization_pct: number
  }>,
) {
  return rows.map((u) => ({
    User: u.full_name || u.username || '—',
    Username: u.username || '—',
    Status: u.is_active ? 'Active' : 'Inactive',
    'Total hrs': Math.round(u.total_hours * 100) / 100,
    'Billable hrs': Math.round(u.billable_hours * 100) / 100,
    'Non-billable hrs': Math.round(u.non_billable_hours * 100) / 100,
    'Util. %': `${(Math.round(u.utilization_pct * 10) / 10).toFixed(1)}%`,
  }))
}

export function getProjectsExportRows(userStats: ComputedEmployeeUserStats) {
  const rows: Record<string, string | number>[] = []
  if (!userStats.projectsData?.length || !userStats.monthlyData?.length) return rows
  userStats.projectsData.forEach((proj) => {
    const projectName = proj.project_name || `Project ${proj.project_id}`
    userStats.monthlyData.forEach((month) => {
      const p = month.projects_breakdown?.find(
        (b) => b.project_id === proj.project_id,
      )
      if (!p) return
      const total = parseFloat(String(p.total_hours || 0))
      const billable = parseFloat(String(p.billable_hours || 0))
      const nonBillable = parseFloat(String(p.non_billable_hours || 0))
      const util = total > 0 ? (billable / total) * 100 : 0
      const periodLabel = `${MONTH_NAMES[(month.month || 1) - 1]} ${month.year}`
      rows.push({
        Project: projectName,
        Period: periodLabel,
        'Total Hrs': Math.round(total * 100) / 100,
        Billable: Math.round(billable * 100) / 100,
        'Non-Bill.': Math.round(nonBillable * 100) / 100,
        'Util. %': Math.round(util * 10) / 10,
      })
    })
  })
  return rows
}
